import { useCallback, useState } from "react";
import { DocumentData } from "../../components/documents/DocumentCollector/types";
import { useUpdateDocumentDetailsMutation } from "../../graphql/supergraphHooks";
import { type DocumentValueInput } from "../../graphql/supergraphTypes";
import { clientContext } from "../../utils/graphql_utils";

/**
 * A hook that provides functionality for updating document metadata.  ProfileID to be taken
 * from the 'meta' field of the documents.
 *
 * @param caseId - The ID of the case the documents belong to
 * @returns Object containing function to update document metadata and loading state
 */
export default function useUpdateMultiDocumentMetadata(caseId: number | undefined) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateDocumentDetails, { loading: updateLoading }] = useUpdateDocumentDetailsMutation({
    context: clientContext(),
    refetchQueries: ["GetDocumentDetails"],
    awaitRefetchQueries: true,
  });

  /**
   * Updates metadata for multiple documents in parallel
   * - Sets document purpose (baseType)
   * - Associates with profile
   * - Adds metadata from subType
   *
   * @param documents - Documents with metadata to update (only those with IDs are processed)
   */
  const updateDocumentMetadata = useCallback(
    async (documents: DocumentData[]) => {
      if (documents.length === 0 || !caseId) {
        return;
      }

      setIsUpdating(true);
      try {
        const updates = documents
          // kind of a hack, but we want the first document to be the last one submitted so that when this
          // mutation triggers compliance to send SNS messages to main, the first document will be the last one
          // processed, which will make it treated as the subscription's (single) tax document
          // Eventually we won't need this when we have multi-doc support on main
          .reverse()
          .filter((data) => !!data.id)
          .map(async (data) => {
            const metadata: DocumentValueInput[] = data.subType
              ? [
                  {
                    key: "type",
                    value: data.subType || "",
                  },
                ]
              : [];
            await updateDocumentDetails({
              variables: {
                caseId,
                documentId: data.id!,
                updateInfo: {
                  profileId: data.meta?.profileId,
                  documentPurpose: data.baseType,
                  metadata: metadata,
                },
              },
            });
          });

        await Promise.all(updates);
      } finally {
        setIsUpdating(false);
      }
    },
    [caseId, updateDocumentDetails],
  );

  return {
    updateDocumentMetadata,
    loading: isUpdating || updateLoading,
  };
}
